// Cached snapshot of GET /global-settings, scoped to the fields the UI
// reads synchronously (language, timezone, temperature unit). Populated
// once at login by ProductSelection so the header + LanguageProvider can
// boot without waiting for an extra round-trip. Refreshed by the
// Global Settings save handler.
//
// Single source of truth — anything that wants to read or update the
// current language/timezone/unit must go through this module. The
// LanguageContext reads `defaultLanguage` from the same blob, so the
// header dropdown and the Global Settings form stay in lockstep.

import { GLOBAL_SETTINGS_CACHE_STORAGE_KEY } from './constants'

// Shape stored under the cache key. Only the three fields the UI reads
// synchronously are listed here — the rest of the GET payload lives in
// the React Query cache for Global Settings and is not mirrored.
const SHAPE = {
  defaultLanguage: 'en',
  defaultTimezone: 'UTC',
  temperatureUnit: 'CELSIUS',
}

// Tiny pub/sub — consumers (e.g. useTemperatureUnit) listen for cache
// mutations triggered outside of React (ProductSelection prefetch,
// Global Settings save). Returns an unsubscribe function.
const listeners = new Set()
const notify = () => listeners.forEach((fn) => fn())
export const subscribeCache = (fn) => {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function safeParse(raw) {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

// Read the cached snapshot. Returns the default shape (not null) so
// callers can dereference fields without null-checks.
export const globalSettingsStorage = {
  getCached: () => {
    const raw = localStorage.getItem(GLOBAL_SETTINGS_CACHE_STORAGE_KEY)
    const parsed = safeParse(raw)
    return { ...SHAPE, ...(parsed || {}) }
  },

  // Merge `next` into the existing snapshot so partial updates (e.g. the
  // header dropdown mutating only `defaultLanguage`) don't blank out the
  // timezone / unit fields.
  setCached: (next) => {
    if (!next || typeof next !== 'object') return
    const merged = { ...globalSettingsStorage.getCached(), ...next }
    localStorage.setItem(GLOBAL_SETTINGS_CACHE_STORAGE_KEY, JSON.stringify(merged))
    notify()
  },

  setDefaultLanguage: (code) => {
    if (!code) return
    globalSettingsStorage.setCached({ defaultLanguage: code })
  },

  clearCached: () => {
    localStorage.removeItem(GLOBAL_SETTINGS_CACHE_STORAGE_KEY)
    notify()
  },
}
